/** Prefer one atomic RLS search snapshot; preserve compatibility during database rollout. */
import { searchGlobal as legacySearch, resolveGlobalSearchSelection as legacySelect,
  warmProjectSearchCandidates as legacyWarm, resolveProjectSearchCandidateHints as legacyHints } from './globalSearchLegacy';
import { searchGlobalRpc } from './globalSearchRpc';
import { buildPrettyProjectPath, buildPrettyTaskPath } from '../utils/prettyRoutes';
import { currentGlobalSearchCandidateGeneration, rememberGlobalSearchCandidates, rememberGlobalSearchTaskCandidates } from './globalSearchCandidates';
import type { GlobalSearchRequest, GlobalSearchResult, GlobalSearchType, GlobalSearchPage } from './globalSearchLegacy';
export type { GlobalSearchRequest, GlobalSearchResult, GlobalSearchType, GlobalSearchPage } from './globalSearchLegacy';
export { literalSearchPattern } from './globalSearchLegacy';
export { isGlobalSearchLegacyBackend } from './globalSearchRpc';

type Cursors = Partial<GlobalSearchPage['cursors']>;
async function legacyPage(request: GlobalSearchRequest, cursors: Cursors, types: GlobalSearchType[], carried: GlobalSearchResult[]) {
  const page = await legacySearch(request, cursors, types, carried);
  return { ...page, cursors: { ...page.cursors, backend: 'legacy' as const } };
}
async function rereadCarried(request: GlobalSearchRequest, carried: GlobalSearchResult[]): Promise<GlobalSearchResult[]> {
  const lookups: Array<{ type: GlobalSearchType; ids: string[] }> = [];
  for (const type of ['project', 'task'] as const) {
    const ids = [...new Set(carried.filter(item => item.type === type).map(item => item.id))];
    for (let offset = 0; offset < ids.length; offset += 20) lookups.push({ type, ids: ids.slice(offset, offset + 20) });
  }
  // Bounded concurrency; old rows supply IDs only. Every returned label passes current RLS and grants.
  let index = 0;
  const results: GlobalSearchResult[][] = [];
  await Promise.all(Array.from({ length: Math.min(4, lookups.length) }, async () => {
    while (index < lookups.length) {
      const item = lookups[index++];
      const page = await searchGlobalRpc(request, {}, [item.type],
        item.type === 'project' ? { projectIds: item.ids } : { taskIds: item.ids });
      if (!page) throw new Error('Search is temporarily unavailable.');
      results.push(page.results);
    }
  }));
  const fresh = new Map(results.flat().map(item => [`${item.type}:${item.id}`, item]));
  return carried.flatMap(item => {
    const checked = fresh.get(`${item.type}:${item.id}`);
    return checked ? [checked] : [];
  });
}
export async function searchGlobal(request: GlobalSearchRequest,
  cursors: Cursors = {}, sourceTypes: GlobalSearchType[] = ['project', 'task'],
  carried: GlobalSearchResult[] = []): Promise<GlobalSearchPage> {
  // The old Tasks cursor addresses two branches; the RPC cursor addresses one merged stream.
  // Keep the provenance until a new query resets paging, including across database rollouts.
  if (cursors.backend === 'legacy') return legacyPage(request, cursors, sourceTypes, carried);
  const generation = currentGlobalSearchCandidateGeneration();
  const page = await searchGlobalRpc(request, cursors, sourceTypes);
  if (!page) {
    if (cursors.backend === 'rpc' || carried.length) throw new Error('Search is temporarily unavailable.');
    return legacyPage(request, cursors, sourceTypes, carried);
  }
  if (!request.signal?.aborted) {
    rememberGlobalSearchCandidates(request.actorId, request.workspaceId,
      page.results.filter(item => item.type === 'project' && item.workspaceId).map(item => ({
        id: item.id, name: item.label, workspace_id: item.workspaceId!, contract_number: null, project_number: null,
      })), generation);
    rememberGlobalSearchTaskCandidates(request.actorId, request.workspaceId,
      page.results.filter(item => item.type === 'task').map(item => ({ id: item.id, title: item.label })), generation);
  }
  if (!carried.length) return page;
  const refreshed = await rereadCarried(request, carried);
  return { ...page, results: [...new Map([...refreshed, ...page.results].map(item => [`${item.type}:${item.id}`, item])).values()] };
}

export async function resolveGlobalSearchSelection(request: GlobalSearchRequest, result: GlobalSearchResult): Promise<{ url: string } | null> {
  // A new caller-bound snapshot checks current access and names, even when the row was renamed.
  const page = await searchGlobalRpc({ ...request, query: '' }, {}, [result.type],
    result.type === 'project' ? { projectIds: [result.id] } : { taskIds: [result.id] });
  if (!page) return legacySelect(request, result);
  const fresh = page.results.find(item => item.type === result.type && item.id === result.id);
  if (!fresh) return null;
  if (fresh.type === 'task') return { url: buildPrettyTaskPath({ taskTitle: fresh.label, taskId: fresh.id }) };
  if (!fresh.clientName) return null;
  return { url: buildPrettyProjectPath({ clientName: fresh.clientName, projectName: fresh.label }) };
}

export async function warmProjectSearchCandidates(request: GlobalSearchRequest, generation: number): Promise<void> {
  // Metadata only, off the typing path. IDs still require a new caller-bound snapshot.
  await legacyWarm(request, generation);
}
export async function resolveProjectSearchCandidateHints(request: GlobalSearchRequest, ids: string[], taskIds: string[] = []): Promise<GlobalSearchResult[]> {
  const types: GlobalSearchType[] = [...(ids.length ? ['project' as const] : []), ...(taskIds.length ? ['task' as const] : [])];
  if (!types.length) return [];
  const page = await searchGlobalRpc(request, {}, types, {
    ...(ids.length ? { projectIds: ids.slice(0, 20) } : {}),
    ...(taskIds.length ? { taskIds: taskIds.slice(0, 20) } : {}),
  });
  return page ? page.results : ids.length ? legacyHints(request, ids) : [];
}
